import type { OnboardingTrackerMode, OnboardingVisibleMode } from '@doneverse/data/utils'
import {
  ONBOARDING_GATE_STEPS,
  ONBOARDING_STEP_COPY,
  type OnboardingStepNumber,
  type OnboardingStepState,
  type OnboardingStepStatus,
} from '@doneverse/shared'
import { Link, useLocation } from '@tanstack/react-router'
import {
  ArrowRight,
  Check,
  ChevronUp,
  Clock,
  Lock,
  RotateCcw,
  Trophy,
  Unlock,
  type LucideIcon,
} from 'lucide-react'
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import styles from './OnboardingPanel.module.css'
import { useOnboardingShell } from './OnboardingShellContext'
import { StepCta } from './OnboardingStepCta'
import { ONBOARDING_STEP_ICONS, OnboardingStepRail } from './OnboardingStepRail'
import { Button } from '@/components/ui/button'
import { useOnboarding } from '@/hooks/useOnboarding'
import { onboardingStepForPath } from '@/lib/onboardingLock'
import { getHomeRoute } from '@/lib/routes'

/**
 * The founder onboarding tracker — ClickUp 86d3uknr9.
 *
 * One card, the first thing on every page, in all three phases:
 *
 *   locked (steps 1–4)  — the panel leads the page and `PageLayout` dims and
 *                         `inert`s everything below it. The dashboard is not
 *                         the founder's yet, and this is the only live thing on
 *                         the screen.
 *   unlocked (6–11)     — the same card, non-blocking, over a live page.
 *   minimized (past 11) — the congratulations card, until the founder puts it
 *                         away and it becomes the pill in the shell.
 *
 * Rendered by `PageLayout`, so it is remounted on navigation and holds no state
 * that must survive one — the surfaces it opens and the "put away" bit both live
 * in `OnboardingShellContext`, provided by the shell. Everything else is derived
 * from `/v1/me`, so a reload always lands in the same place.
 *
 * Behind the `founder-onboarding` flag, which `useOnboarding` folds in: with it
 * off the mode is `hidden` and this returns before touching layout.
 */
export function OnboardingPanel({ compact = false }: { compact?: boolean }) {
  const { mode, nextUp, trackerSteps, progress } = useOnboarding()
  const shell = useOnboardingShell()
  const bodyId = useId()
  const { pathname } = useLocation()

  // Which step the founder has ASKED to read about, which is only ever a step
  // with nowhere to go — the ones with pages of their own are links, and where
  // they lead is the answer instead (see `onboardingStepForPath`). Display state
  // only: it never reaches the server, and it does not outlive a navigation,
  // which is why the route and not this is the fallback.
  const [picked, setPicked] = useState<OnboardingStepNumber | null>(null)
  const previousStep = useRef<{ nextUp: OnboardingStepNumber | null; mode: OnboardingTrackerMode } | null>(
    null,
  )
  const advanced = useProgressPulse(progress.completed)

  // `hidden` is "this user has no onboarding at all". Split off here so the
  // mode-keyed copy below is a total lookup rather than one that can miss.
  const panelMode: OnboardingVisibleMode | null = mode === 'hidden' ? null : mode

  // Whether this panel draws anything at all. One predicate rather than a
  // condition beside the effect and another at the early return: the shell
  // counts mounted panels to tell "the founder can see what to do next" from
  // "the founder is stranded", so a panel that rendered null while still
  // registering itself would suppress the rescue with nothing on screen.
  //
  // The last clause is the panel put away by the founder, or squeezed off a
  // surface that manages its own scroll — the pill in the shell is the way back
  // in both cases.
  const visible =
    panelMode !== null &&
    trackerSteps.length > 0 &&
    !(panelMode === 'minimized' && (compact || shell?.panelDismissed))

  // Tell the shell this page is showing the tracker, so it knows the founder
  // has something to act on here — see `panelMounted` — and hand it the element,
  // which is what `revealPanel` scrolls back to. Registered from the top of the
  // component, before the early return, because a hook cannot run after one.
  //
  // The compact row registers no element: it is pinned to the top of a surface
  // that scrolls under it, so it is never off-screen to be brought back.
  const panelRef = useRef<HTMLElement | null>(null)
  const registerPanel = shell?.registerPanel
  useEffect(() => {
    if (!registerPanel || !visible) return
    return registerPanel(panelRef.current)
    // `compact` picks which shape rendered, and so whether there is an element
    // to register at all.
  }, [registerPanel, visible, compact])

  // When a step closes under the founder — their own click, or a webhook — the
  // panel STAYS on the step they were watching, so it can show them the tick
  // for what they just did rather than silently swapping the card. What is next
  // is not withheld by that: the completed card carries the NEXT step's own CTA
  // (see `StepBody`), so the founder gets the receipt and the way onwards at
  // once. Only when they had made no choice of their own: an explicit pick is
  // theirs to keep.
  //
  // A phase change clears the selection instead — including one the founder
  // made. The unlock and the finish each land their own celebration and open a
  // different chapter, so whatever step they had been reading about is no
  // longer the point: after the unlock it is where to go next, and after the
  // last step it is that they are done.
  useEffect(() => {
    const previous = previousStep.current
    previousStep.current = { nextUp, mode }
    if (!previous) return
    if (previous.mode !== mode) {
      setPicked(null)
      return
    }
    if (previous.nextUp === null || previous.nextUp === nextUp || nextUp === null) return
    setPicked((current) => current ?? previous.nextUp)
  }, [nextUp, mode])

  if (panelMode === null || !visible) return null

  const lastStep = trackerSteps[trackerSteps.length - 1].stepNumber
  // An explicit pick, else the step whose page this is, else what the server
  // says is next — the last of which is what a page belonging to no step (the
  // founder home, anywhere else in the app) lands on.
  const selected = picked ?? onboardingStepForPath(trackerSteps, pathname) ?? nextUp ?? lastStep
  const selectedIndex = trackerSteps.findIndex((step) => step.stepNumber === selected)
  const selectedState = trackerSteps[selectedIndex]
  if (!selectedState) return null

  if (compact) {
    return <CompactPanel nextUp={nextUp} progress={progress} advanced={advanced} />
  }

  // The finish is the whole card until the founder asks to see a step, which
  // is what a rail node click means here — the journey, still there to look
  // back at, exactly as the pill's popover used to offer.
  const celebrating = panelMode === 'minimized' && picked === null

  return (
    // A named region, so a screen reader can jump to the tracker the way a
    // sighted founder's eye does — it is the first thing on every page.
    <section ref={panelRef} className={styles.panel} aria-label={PANEL_LABEL}>
      <div className={styles.card}>
        <header className={styles.head}>
          <div className={styles.heading}>
            <p className={styles.eyebrow}>Founder onboarding</p>
            <h2 className={styles.headline}>{HEADLINE[panelMode]}</h2>
          </div>
          <div className={styles.headActions}>
            <ProgressReadout progress={progress} advanced={advanced} />
            {panelMode === 'minimized' && (
              <button type="button" className={styles.hide} onClick={() => shell?.dismissPanel()}>
                <ChevronUp className={styles.hideIcon} aria-hidden />
                Hide
              </button>
            )}
          </div>
        </header>

        <OnboardingStepRail steps={trackerSteps} selected={selected} onSelect={setPicked} controls={bodyId} />

        {celebrating ? (
          <FinishedBody id={bodyId} onDismiss={() => shell?.dismissPanel()} />
        ) : (
          <StepBody
            id={bodyId}
            step={selected}
            status={selectedState.status}
            position={selectedIndex + 1}
            total={trackerSteps.length}
            next={trackerSteps[selectedIndex + 1] ?? null}
            onGoToNext={setPicked}
          />
        )}
      </div>

      <FootNote mode={panelMode} />
    </section>
  )
}

/**
 * How far along the journey is — the one readout the full card and the compact
 * row share, so a change to it (a state, an attribute, the wording) cannot land
 * on only one of them.
 */
function ProgressReadout({
  progress,
  advanced,
}: {
  progress: { completed: number; total: number }
  advanced: boolean
}) {
  return (
    <span
      className={styles.progress}
      data-complete={progress.completed === progress.total ? 'true' : undefined}
      data-advanced={advanced ? 'true' : undefined}
    >
      <span className={styles.progressDot} aria-hidden />
      {progress.completed} of {progress.total} complete
    </span>
  )
}

/**
 * The one green thing on the card.
 *
 * Tailwind utilities passed through `Button`'s `className` rather than a rule in
 * the stylesheet, so `cn`'s tailwind-merge DROPS the button's own `bg-primary`
 * instead of racing it on specificity — the same way the Doing Blueprint's
 * accent CTA is built.
 */
const CTA_ACCENT = 'bg-accent text-accent-foreground hover:bg-accent/90'

/** Names the region in both its shapes — the full card and the compact row. */
const PANEL_LABEL = 'Founder onboarding'

const HEADLINE: Record<OnboardingVisibleMode, string> = {
  locked: 'Complete these steps to unlock your Dashboard',
  unlocked: 'Complete these steps to launch with your Doer',
  minimized: "You're all set up!",
}

/**
 * What the line under the card says about the page it leads. Silent through the
 * middle of the journey, where the page is simply the page — so `unlocked` is
 * deliberately absent rather than mapped to an empty string.
 */
const FOOT_NOTE: Partial<Record<OnboardingVisibleMode, { icon: LucideIcon; text: string }>> = {
  locked: { icon: Lock, text: 'Complete your setup to unlock your Dashboard' },
  minimized: { icon: Unlock, text: 'Dashboard unlocked' },
}

/**
 * The selected step: what it is, what it costs, and the one thing to press.
 *
 * Three statuses, three different offers — and the complete one is not a dead
 * end. A founder who has just finished a step is looking at a receipt, so the
 * action slot carries the NEXT step's own CTA: the tick and the way onwards on
 * one card, in one click. See {@link StepAction}.
 */
function StepBody({
  id,
  step,
  status,
  position,
  total,
  next,
  onGoToNext,
}: {
  id: string
  step: OnboardingStepNumber
  status: OnboardingStepStatus
  position: number
  total: number
  /** The step after this one in the rail, which the complete state hands on to. */
  next: OnboardingStepState | null
  onGoToNext: (step: OnboardingStepNumber) => void
}) {
  const shell = useOnboardingShell()
  const titleId = useId()
  const copy = ONBOARDING_STEP_COPY[step]
  const Icon = ONBOARDING_STEP_ICONS[step]
  // Only the pre-unlock steps gate anything: after the unlock 6/7/8/9 are open
  // at once and finishing one opens nothing that was not already open, so the
  // promise would be false.
  const unlocksNext = (ONBOARDING_GATE_STEPS as readonly number[]).includes(step) && position < total

  return (
    // A named group, so the panel body announces as "Take the product tour"
    // when a rail node swaps what it is describing.
    <div className={styles.step} id={id} role="group" aria-labelledby={titleId} data-status={status}>
      <span className={styles.stepIcon} aria-hidden>
        <Icon />
      </span>

      <div className={styles.stepText}>
        <p className={styles.stepEyebrow}>
          Step {position} of {total}
        </p>
        <h3 id={titleId} className={styles.stepTitle}>
          {copy.title}
        </h3>
        <p className={styles.stepDescription}>{copy.description}</p>

        <div className={styles.stepMeta}>
          {status === 'complete' ? (
            <>
              {/* Said in words, because the only other mark of a finished step
                  in this card is the icon turning green — and the action beside
                  it now names a DIFFERENT step, so "which one is done" cannot be
                  left to a colour. */}
              <span className={styles.chip}>
                <Check className={styles.chipIcon} aria-hidden />
                Completed
              </span>
              {/* Only the tour can honestly be re-run: it is the one step whose
                  surface is ours to replay, and re-opening it completes nothing
                  that is not already complete. */}
              {copy.surface === 'product-tour' && (
                <button
                  type="button"
                  className={styles.chipButton}
                  onClick={() => shell?.openSurface('product-tour', { replay: true })}
                >
                  <RotateCcw className={styles.chipIcon} aria-hidden />
                  Restart
                </button>
              )}
            </>
          ) : (
            <>
              {copy.estimatedMinutes !== undefined && (
                <span className={styles.chip}>
                  <Clock className={styles.chipIcon} aria-hidden />
                  About {copy.estimatedMinutes} min
                </span>
              )}
              {unlocksNext && (
                <span className={styles.chip}>
                  <Unlock className={styles.chipIcon} aria-hidden />
                  Unlocks Step {position + 1}
                </span>
              )}
              {status === 'locked' && (
                <span className={styles.chip}>
                  <Lock className={styles.chipIcon} aria-hidden />
                  Unlocks when the steps before it are done
                </span>
              )}
            </>
          )}
        </div>
      </div>

      <div className={styles.stepAction}>
        {status === 'active' && <StepCta step={step} size="lg" className={CTA_ACCENT} />}
        {status === 'complete' && next !== null && <StepAction next={next} onGoToNext={onGoToNext} />}
      </div>
    </div>
  )
}

/**
 * What a founder presses on a step they have already finished.
 *
 * The step after it is USUALLY the active one — that is what "you just finished
 * this" means — and then this is that step's own CTA. So the green button on the
 * card is the real thing and says where it goes ("Book kick-off call", "Start
 * the tour"): one click rather than two, and no button dressed as the action
 * that only re-labels the card it sits on.
 *
 * The other case is a founder reading back through their own journey: they
 * clicked a rail node several steps back, and the step after THAT one is
 * finished too. There is nothing to press, so the button walks the panel forward
 * instead — which is also the only safe thing it can do, because a founder who
 * is still locked has every route but the active step's bounced by
 * `isAllowedWhileOnboardingLocked`, and a link would land them somewhere they
 * never asked to go.
 *
 * A next step that is active with nothing to press (step 11, which the workflow
 * closes) falls to `StepCta`'s waiting state — the same answer the old button
 * gave after a click, one click sooner.
 */
function StepAction({
  next,
  onGoToNext,
}: {
  next: OnboardingStepState
  onGoToNext: (step: OnboardingStepNumber) => void
}) {
  if (next.status === 'active') {
    return <StepCta step={next.stepNumber} size="lg" className={CTA_ACCENT} />
  }

  return (
    <Button size="lg" className={CTA_ACCENT} onClick={() => onGoToNext(next.stepNumber)}>
      Next step
      <ArrowRight aria-hidden />
    </Button>
  )
}

/** The end of the journey, on the page rather than over it. */
function FinishedBody({ id, onDismiss }: { id: string; onDismiss: () => void }) {
  return (
    <div className={styles.finished} id={id}>
      <span className={styles.finishedIcon} aria-hidden>
        <Trophy />
      </span>
      <h3 className={styles.finishedTitle}>Congratulations — you&apos;re all set!</h3>
      <p className={styles.finishedBody}>Your Dashboard is fully unlocked. Time to go get stuff done.</p>
      {/* Dismisses as it navigates: there is nothing left to track, and a
          congratulations card on every page forever is a nag, not a reward.
          "Go Live" rather than "Go to Dashboard" so this and the finish moment's
          button are visibly the same act (ClickUp 86d40tuc8) — a founder who
          dismissed the moment and meets this card later should not have to work
          out whether it does something different. */}
      <Button asChild size="lg" className={CTA_ACCENT}>
        <Link to={getHomeRoute('founder')} onClick={onDismiss}>
          Go Live
          <ArrowRight aria-hidden />
        </Link>
      </Button>
    </div>
  )
}

/**
 * The line between the panel and the page it leads, which says what the state of
 * the page below actually is.
 */
function FootNote({ mode }: { mode: OnboardingVisibleMode }) {
  const note = FOOT_NOTE[mode]
  if (!note) return null

  return (
    <p className={styles.footNote}>
      <note.icon className={styles.footNoteIcon} aria-hidden />
      {note.text}
    </p>
  )
}

/**
 * One row for a surface that owns its own scroll — a lesson player, a table that
 * fills the viewport. The full card would take a fifth of the screen from a
 * page built to use all of it, so what survives is the progress, the next step,
 * and the button.
 */
function CompactPanel({
  nextUp,
  progress,
  advanced,
}: {
  nextUp: OnboardingStepNumber | null
  progress: { completed: number; total: number }
  advanced: boolean
}) {
  const ref = useCompactHeight()

  return (
    <div ref={ref} className={styles.compact} role="region" aria-label={PANEL_LABEL}>
      <ProgressReadout progress={progress} advanced={advanced} />
      {nextUp !== null && (
        <>
          <span className={styles.compactNext}>
            Next: <strong>{ONBOARDING_STEP_COPY[nextUp].title}</strong>
          </span>
          <span className={styles.compactAction}>
            <StepCta step={nextUp} size="sm" className={CTA_ACCENT} />
          </span>
        </>
      )}
    </div>
  )
}

/**
 * Publish the compact row's height on the root element.
 *
 * Only the compact one needs this, and only because of what it sits on. The
 * full card is content among content — the page simply starts below it. The
 * surfaces that get the compact row instead are the ones that own their own
 * layout, and one of them (the lesson player) anchors a sidebar to the VIEWPORT
 * just under the page header, where it happily paints over anything the flow
 * puts there. So it is told how much chrome to clear rather than guessing.
 */
function useCompactHeight() {
  const ref = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    const row = ref.current
    if (!row || typeof document === 'undefined') return

    // The observer fires per frame while a window resize is dragged, and each
    // write invalidates style for everything reading the var. The row's height
    // only actually changes when it wraps, so only a changed value is published.
    let published = ''
    const publish = () => {
      const height = `${row.offsetHeight}px`
      if (height === published) return
      published = height
      document.documentElement.style.setProperty('--onboarding-compact-h', height)
    }
    publish()
    const observer = new ResizeObserver(publish)
    observer.observe(row)
    return () => {
      observer.disconnect()
      document.documentElement.style.removeProperty('--onboarding-compact-h')
    }
  }, [])

  return ref
}

/**
 * True for a beat after the completed count goes up.
 *
 * The panel is remounted on every navigation, so this only ever fires where it
 * means something: the founder was sitting on the page when a step closed. It
 * marks the moment and gets out of the way — there is no persistence, because
 * a celebration that survives a reload is not a celebration.
 */
const PROGRESS_PULSE_MS = 2400

function useProgressPulse(completed: number): boolean {
  const [pulsing, setPulsing] = useState(false)
  const previous = useRef<number | null>(null)

  useEffect(() => {
    const before = previous.current
    previous.current = completed
    if (before === null || completed <= before) return

    setPulsing(true)
    const timer = window.setTimeout(() => setPulsing(false), PROGRESS_PULSE_MS)
    return () => window.clearTimeout(timer)
  }, [completed])

  return pulsing
}
